/**
 * Values on the administrator screens (iteration 2): numbers in full, dates
 * «19.10.2026», times in Kyiv (the market's timezone), phone, region,
 * Ukrainian plurals. Pure — no React.
 */
import type { Locale, MessageKey } from '../../i18n/messages.js';

/** Dates and times of the administrator screens: Kyiv, like the market. */
export const ADMIN_TIMEZONE = 'Europe/Kyiv';

const NUMBER_LOCALE: Record<Locale, string> = { uk: 'uk-UA', en: 'en-GB' };

/** `{name}` placeholders of a message → values. */
export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: ADMIN_TIMEZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function kyivParts(iso: string): Record<'day' | 'month' | 'year' | 'hour' | 'minute', string> {
  const parts = Object.fromEntries(DATE_TIME.formatToParts(new Date(iso)).map((p) => [p.type, p.value]));
  return {
    day: parts.day ?? '',
    month: parts.month ?? '',
    year: parts.year ?? '',
    hour: parts.hour ?? '',
    minute: parts.minute ?? '',
  };
}

/** Timestamp → «20.10.2026» (Kyiv). */
export function formatDate(iso: string): string {
  const p = kyivParts(iso);
  return `${p.day}.${p.month}.${p.year}`;
}

/** Timestamp → «15:02» (Kyiv). */
export function formatTime(iso: string): string {
  const p = kyivParts(iso);
  return `${p.hour}:${p.minute}`;
}

/** Timestamp → «20.10.2026, 15:02» (Kyiv). */
export function formatDateTime(iso: string): string {
  return `${formatDate(iso)}, ${formatTime(iso)}`;
}

/** Calendar day `YYYY-MM-DD` → «21.10.2026». */
export function formatCalendarDate(ymd: string): string {
  const [y, m, d] = ymd.split('-');
  return `${d}.${m}.${y}`;
}

/** Number in full with a fixed count of decimals: «90,8», «3 914». */
export function formatNumber(value: number, locale: Locale, digits = 0): string {
  return new Intl.NumberFormat(NUMBER_LOCALE[locale], {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/** Ukrainian numbers as «+380 67 123 45 67»; anything else as entered. */
export function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const national = digits.length === 12 && digits.startsWith('380') ? digits.slice(3) : null;
  const local = digits.length === 10 && digits.startsWith('0') ? digits.slice(1) : null;
  const n = national ?? local;
  if (!n) return raw;
  return `+380 ${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5, 7)} ${n.slice(7)}`;
}

/** Oblasts as the object stores them (`DSO_OPERATORS[].region`) → English. */
const OBLASTS: Record<string, string> = {
  Вінницька: 'Vinnytsia',
  Волинська: 'Volyn',
  Дніпропетровська: 'Dnipropetrovsk',
  Донецька: 'Donetsk',
  Житомирська: 'Zhytomyr',
  Закарпатська: 'Zakarpattia',
  Запорізька: 'Zaporizhzhia',
  'Івано-Франківська': 'Ivano-Frankivsk',
  Київська: 'Kyiv',
  Кіровоградська: 'Kirovohrad',
  Луганська: 'Luhansk',
  Львівська: 'Lviv',
  Миколаївська: 'Mykolaiv',
  Одеська: 'Odesa',
  Полтавська: 'Poltava',
  Рівненська: 'Rivne',
  Сумська: 'Sumy',
  Тернопільська: 'Ternopil',
  Харківська: 'Kharkiv',
  Херсонська: 'Kherson',
  Хмельницька: 'Khmelnytskyi',
  Черкаська: 'Cherkasy',
  Чернівецька: 'Chernivtsi',
  Чернігівська: 'Chernihiv',
};
const CITIES: Record<string, string> = { Київ: 'Kyiv city', Севастополь: 'Sevastopol city' };

/** «Київська» → «Київська обл.» / «Kyiv region» (as the object form); a city or an unknown name as stored. */
export function regionLabel(region: string, locale: Locale): string {
  const name = region.replace(/\s+(область|обл\.)$/u, '').trim();
  const oblast = OBLASTS[name];
  if (oblast) return locale === 'uk' ? `${name} обл.` : `${oblast} region`;
  const city = CITIES[name];
  if (city) return locale === 'uk' ? name : city;
  return region;
}

/** Inverter model as on the prototype: «Deye SUN-12K-SG04LP3». */
export function modelLabel(model: string): string {
  return /^deye\b/i.test(model) ? model : `Deye ${model}`;
}

/** Message keys with Ukrainian plural forms: `<base>.one|few|many`. */
export type CountBase =
  | 'operator.count.objects'
  | 'operator.count.clients'
  | 'operator.count.applications'
  | 'operator.count.values';

/** «1 об'єкт», «3 об'єкти», «5 об'єктів» (English: one / other). */
export function countLabel(t: (k: MessageKey) => string, locale: Locale, base: CountBase, n: number): string {
  const category = new Intl.PluralRules(locale).select(n);
  const form = category === 'one' ? 'one' : category === 'few' ? 'few' : 'many';
  return fill(t(`${base}.${form}`), { n: formatNumber(n, locale) });
}
